import { useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowRight, ArrowDown } from "lucide-react";
import { MEDIA } from "@/data/media";

const EASE = [0.22, 1, 0.36, 1];

const fadeBlur = (delay, reduceMotion) => ({
  initial: reduceMotion
    ? { opacity: 1, filter: "blur(0px)", y: 0 }
    : { opacity: 0, filter: "blur(10px)", y: 14 },
  animate: { opacity: 1, filter: "blur(0px)", y: 0 },
  transition: reduceMotion
    ? { duration: 0 }
    : { duration: 1.2, ease: EASE, delay },
});

const DEFAULT_TAGLINE = Object.freeze({
  lead: "Strong body.",
  middle: "Clear mind.",
  end: "Confident you.",
});

const DEFAULT_PRIMARY_ACTION = Object.freeze({
  label: "Explore the Digital Studio",
  href: "/digital-studio",
  testId: "hero-digital-studio-button",
  icon: "up-right",
});

const HeroAction = ({ action }) => {
  const Icon =
    action.icon === "down"
      ? ArrowDown
      : action.icon === "right"
        ? ArrowRight
        : ArrowUpRight;
  const content = (
    <>
      <span>{action.label}</span>
      <span className="hero-pill-badge">
        <Icon size={18} />
      </span>
    </>
  );

  if (action.href) {
    return (
      <a
        data-testid={action.testId}
        href={action.href}
        target={action.target}
        rel={action.rel}
        className="hero-pill"
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      data-testid={action.testId}
      onClick={action.onClick}
      className="hero-pill"
    >
      {content}
    </button>
  );
};

export const HeroVideo = ({
  id = "hero",
  testId = "hero-section",
  title = "FORTE",
  subtitle = "By Ryann",
  tagline = DEFAULT_TAGLINE,
  primaryAction = DEFAULT_PRIMARY_ACTION,
  secondaryAction,
  tertiaryAction,
  note,
  onInquire,
}) => {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const [videoOk, setVideoOk] = useState(Boolean(MEDIA.heroVideo));
  const reduceMotion = useReducedMotion();
  const resolvedSecondaryAction =
    secondaryAction ??
    (onInquire
      ? {
          label: "Inquire for Privates",
          onClick: onInquire,
          testId: "hero-inquire-button",
          icon: "right",
        }
      : null);
  const actions = [
    primaryAction,
    resolvedSecondaryAction,
    tertiaryAction,
  ].filter(Boolean);
  const hasThreeActions = actions.length === 3;

  return (
    <section
      id={id}
      data-testid={testId}
      className="relative flex min-h-screen w-full items-center justify-center overflow-hidden"
    >
      {/* Media layer */}
      <div className="absolute inset-0" style={{ backgroundColor: "var(--bg)" }}>
        {MEDIA.heroVideo && videoOk && (
          <video
            ref={videoRef}
            className="absolute inset-0 h-full w-full object-cover"
            poster={MEDIA.heroPoster}
            muted
            loop
            playsInline
            autoPlay
            preload="auto"
            onCanPlay={() => setVideoReady(true)}
            onPlaying={() => setVideoReady(true)}
            onError={() => setVideoOk(false)}
            style={{
              opacity: videoReady ? 1 : 0,
              transition: "opacity 1.6s ease",
            }}
          >
            {MEDIA.heroVideoWebm && (
              <source src={MEDIA.heroVideoWebm} type="video/webm" />
            )}
            <source src={MEDIA.heroVideo} type="video/mp4" />
          </video>
        )}
        {/* Concentric ring texture */}
        <div className="absolute inset-0 hero-rings" style={{ zIndex: 1 }} />
        {/* Dark overlay for legibility */}
        <div className="absolute inset-0 hero-overlay" style={{ zIndex: 2 }} />
      </div>

      {/* Content */}
      <div
        className="container-fx relative flex flex-col items-center text-center"
        style={{ zIndex: 3 }}
      >
        <motion.h1
          className="display"
          {...fadeBlur(0.15, reduceMotion)}
          style={{
            fontSize: "clamp(4rem, 17vw, 13rem)",
            fontWeight: 700,
            lineHeight: 0.9,
            letterSpacing: "0.02em",
            textShadow: "0 8px 60px rgba(0,0,0,0.55)",
          }}
        >
          {title}
        </motion.h1>

        <motion.div
          className="font-serif accent-italic"
          {...fadeBlur(0.45, reduceMotion)}
          style={{
            fontSize: "clamp(2.2rem, 8.5vw, 6rem)",
            fontStyle: "italic",
            fontWeight: 500,
            lineHeight: 1,
            marginTop: "-0.06em",
            textShadow: "0 6px 40px rgba(0,0,0,0.5)",
          }}
        >
          {subtitle}
        </motion.div>

        <motion.p
          className="mt-9 flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1"
          initial={reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={
            reduceMotion ? { duration: 0 } : { duration: 1, ease: EASE, delay: 0.95 }
          }
          style={{ textShadow: "0 2px 20px rgba(0,0,0,0.6)" }}
        >
          <span
            className="font-serif"
            style={{ color: "var(--text)", fontSize: "1.15rem", fontWeight: 500 }}
          >
            {tagline.lead}
          </span>
          <span
            style={{
              fontFamily: "var(--font-sans)",
              color: "var(--bronze)",
              textTransform: "uppercase",
              letterSpacing: "0.22em",
              fontSize: "0.8rem",
              fontWeight: 600,
            }}
          >
            {tagline.middle}
          </span>
          <span
            className="font-serif"
            style={{
              color: "var(--bronze)",
              fontStyle: "italic",
              fontSize: "1.2rem",
            }}
          >
            {tagline.end}
          </span>
        </motion.p>

        <motion.div
          className={`hero-actions mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row${
            hasThreeActions ? " hero-actions--three" : ""
          }`}
          initial={reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={
            reduceMotion ? { duration: 0 } : { duration: 1, ease: EASE, delay: 1.2 }
          }
        >
          {actions.map((action) => (
            <HeroAction key={action.testId || action.label} action={action} />
          ))}
        </motion.div>

        {note && (
          <motion.p
            className="hero-note font-serif"
            {...fadeBlur(1.35, reduceMotion)}
          >
            {note}
          </motion.p>
        )}
      </div>

      {/* Scroll cue */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        style={{ zIndex: 3, color: "var(--muted)" }}
        initial={reduceMotion ? false : { opacity: 0 }}
        animate={
          reduceMotion ? { opacity: 1 } : { opacity: [0, 1, 1, 0.3, 1] }
        }
        transition={
          reduceMotion ? { duration: 0 } : { duration: 2.4, delay: 1.8, repeat: Infinity }
        }
      >
        <ArrowDown size={18} />
      </motion.div>
    </section>
  );
};
